import { ComponentProps, useContext } from "react";
import { Button as ReactAriaButton } from "react-aria-components";
import { ChevronLeft, ChevronRight } from "react-feather";
import clsx from "clsx";

import { PalletLangContext } from "../lang-provider";

import Select from "./forms/select";
import styles from "./pagination.module.css";
import Search from "./search";

interface PaginationProps {
  "data-testid"?: string;
  currentPage: number;
  className?: string;
  totalPages: number;
  nextButton: {
    isDisabled: boolean;
    onPress: () => void;
  };
  previousButton: {
    isDisabled: boolean;
    onPress: () => void;
  };
  search?: ComponentProps<typeof Search>;
  jumpTo?: (page: number) => void;
}

function pageId(num: number): string {
  return `page-${num}`;
}

function extractPageNum(key: string): number {
  const normalized = key.replace("page-", "");
  return parseInt(normalized, 10);
}

function Pagination({
  "data-testid": testId = "pallet-pagination",
  currentPage,
  totalPages,
  nextButton,
  previousButton,
  jumpTo,
  className,
  search,
}: PaginationProps) {
  const langContext = useContext(PalletLangContext);

  const pageOptions: Array<{ id: string; label: string; key: string }> = [];
  for (let i = 0; i < totalPages; i++) {
    pageOptions.push({ id: pageId(i), label: `${i + 1}`, key: pageId(i) });
  }

  return (
    <div data-testid={testId} className={clsx(styles.pagination, className)}>
      <div className={styles.countAndSearch}>
        <div className={styles.pageCount}>
          <div className={styles.currentPage}>{currentPage}</div>/
          <div>
            {totalPages} {langContext?.simpleTranslate("pagination.pages")}
          </div>
        </div>
        {search && (
          <Search
            className={styles.search}
            autofocus={search?.autofocus || true}
            placeholder={search.placeholder}
            label={search.label}
            value={search.value}
            onChange={search.onChange}
            onClear={search.onClear}
          />
        )}
      </div>

      <div className={styles.paginator}>
        <ReactAriaButton
          aria-label="Previous Page"
          isDisabled={previousButton.isDisabled}
          className={clsx(styles.button, styles.left)}
          onPress={previousButton.onPress}
        >
          <ChevronLeft size={22} />
        </ReactAriaButton>

        <ReactAriaButton
          aria-label="Next Page"
          isDisabled={nextButton.isDisabled}
          className={clsx(styles.button, styles.right)}
          onPress={nextButton.onPress}
        >
          <ChevronRight size={22} />
        </ReactAriaButton>

        {typeof jumpTo === "function" && (
          <Select
            items={pageOptions}
            className={styles.jumpToSelect}
            display={{ orientation: "row" }}
            selectedId={pageId(currentPage - 1)}
            onSelectionChange={(value) =>
              typeof value === "string" && jumpTo?.(extractPageNum(value))
            }
            label={langContext?.simpleTranslate("pagination.jumpTo")}
            name={
              langContext?.simpleTranslate("pagination.pageSelectName") ?? ""
            }
          />
        )}
      </div>
    </div>
  );
}

export default Pagination;
